import {
  BadgeCheck,
  CalendarPlus,
  CheckCircle2,
  ClipboardList,
  Cloud,
  Database,
  FileCheck2,
  FolderKanban,
  HardDrive,
  Link2,
  LockKeyhole,
  Plus,
  ShieldCheck,
  UploadCloud,
  Users,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import {
  createAdminVault,
  generateAdminGuestUploadLink,
  getAdminClients,
  getMedia,
  getMemoryVaultWorkspace,
  updateMediaAction,
} from "../../lib/api";
import { formatBytes, formatDate, statusClass, statusLabel } from "../../lib/format";
import type { ClientRecord, MediaItem, MemoryVaultWorkspace, VaultStatus } from "../../lib/types";

const vaultStatusOptions: Array<{ value: VaultStatus; label: string }> = [
  { value: "not_started", label: "Not Started" },
  { value: "setup_in_progress", label: "Setup In Progress" },
  { value: "active", label: "Active" },
  { value: "needs_review", label: "Needs Review" },
  { value: "completed", label: "Completed" },
];

const vaultStatusLabel = Object.fromEntries(vaultStatusOptions.map((item) => [item.value, item.label])) as Record<
  VaultStatus,
  string
>;

export function AdminMemoryMediaVaultPage() {
  const [vault, setVault] = useState<MemoryVaultWorkspace | null>(null);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<VaultStatus>("setup_in_progress");
  const [selectedEventId, setSelectedEventId] = useState("");
  const [draftMessage, setDraftMessage] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [eventName, setEventName] = useState("");
  const [eventSlug, setEventSlug] = useState("");
  const [vaultDisplayName, setVaultDisplayName] = useState("");
  const [eventType, setEventType] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [optionalLogo, setOptionalLogo] = useState("");
  const [primaryThemeColor, setPrimaryThemeColor] = useState("");
  const [secondaryThemeColor, setSecondaryThemeColor] = useState("");
  const [accentColor, setAccentColor] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [guestUploadInstructions, setGuestUploadInstructions] = useState("");
  const [uploadButtonText, setUploadButtonText] = useState("");
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [approvalNotice, setApprovalNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [workspaceError, setWorkspaceError] = useState("");

  useEffect(() => {
    const load = async () => {
      const [clientData, mediaData] = await Promise.all([
        getAdminClients().catch(() => []),
        getMedia({}).catch(() => []),
      ]);
      setClients(clientData);
      setMedia(mediaData);
      setSelectedClientId(clientData[0]?.id || "");

      try {
        const vaultData = await getMemoryVaultWorkspace();
        setVault(vaultData);
        setSelectedStatus(vaultData.status);
        setSelectedEventId(vaultData.events[0]?.id || "");
        setSelectedClientId(vaultData.clientId || clientData[0]?.id || "");
      } catch (error) {
        setVault(null);
        setWorkspaceError(error instanceof Error ? error.message : "Create a client event before opening a vault workspace.");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const selectedEvent = useMemo(
    () => vault?.events.find((event) => event.id === selectedEventId) || vault?.events[0],
    [selectedEventId, vault],
  );

  const storagePercent = vault ? Math.min(100, Math.round((vault.storageBytes / vault.storageLimitBytes) * 100)) : 0;
  const clientOptions = clients.length
    ? clients.map((client) => ({ id: client.id, name: client.clientName }))
    : vault
      ? [{ id: vault.clientId, name: vault.clientName }]
      : [];
  const pendingMedia = media.filter((item) => item.status === "pending");
  const recentMedia = media.slice(0, 6);
  const selectedGuestLink = useMemo(
    () => vault?.guestUploadLinks.find((link) => link.vaultEventId === selectedEvent?.id) || vault?.guestUploadLinks[0],
    [selectedEvent?.id, vault],
  );

  const createVault = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedClientId) return;
    setBusy(true);
    setDraftMessage("");
    try {
      const result = await createAdminVault({
        clientId: selectedClientId,
        eventName,
        eventSlug,
        vaultDisplayName,
        eventType,
        eventDate,
        coverImage,
        optionalLogo,
        primaryThemeColor,
        secondaryThemeColor,
        accentColor,
        welcomeMessage,
        guestUploadInstructions,
        uploadButtonText,
        confirmationMessage,
        approvalNotice,
      });
      const vaultData = await getMemoryVaultWorkspace({ clientId: selectedClientId, eventId: result.event.id });
      const mediaData = await getMedia({ clientId: selectedClientId, eventId: result.event.id }).catch(() => []);
      setVault(vaultData);
      setMedia(mediaData);
      setSelectedStatus(vaultData.status);
      setSelectedEventId(vaultData.events[0]?.id || "");
      setEventName("");
      setEventSlug("");
      setVaultDisplayName("");
      setEventType("");
      setEventDate("");
      setCoverImage("");
      setOptionalLogo("");
      setPrimaryThemeColor("");
      setSecondaryThemeColor("");
      setAccentColor("");
      setWelcomeMessage("");
      setGuestUploadInstructions("");
      setUploadButtonText("");
      setConfirmationMessage("");
      setApprovalNotice("");
      setDraftMessage(
        `Vault created for ${result.event.eventName}. Guest upload link: ${
          result.guestUploadLink.share_url || result.guestUploadLink.share_url_placeholder || "generated"
        }`,
      );
    } catch (error) {
      setDraftMessage(error instanceof Error ? error.message : "Unable to create event vault.");
    } finally {
      setBusy(false);
    }
  };

  const regenerateLink = async () => {
    if (!vault || !selectedEvent) return;
    setBusy(true);
    setDraftMessage("");
    try {
      const result = await generateAdminGuestUploadLink({
        clientId: vault.clientId,
        eventId: selectedEvent.eventId,
        guestUploadLinkId: selectedGuestLink?.id,
        regenerate: Boolean(selectedGuestLink?.id),
      });
      const vaultData = await getMemoryVaultWorkspace({ clientId: vault.clientId, eventId: selectedEvent.eventId });
      setVault(vaultData);
      setDraftMessage(`Guest upload link generated: ${result.link.share_url || result.link.share_url_placeholder || result.link.id}`);
    } catch (error) {
      setDraftMessage(error instanceof Error ? error.message : "Unable to generate guest upload link.");
    } finally {
      setBusy(false);
    }
  };

  const updateReviewStatus = async (id: string, action: "approve" | "hide" | "not-approve") => {
    try {
      const result = await updateMediaAction(id, action);
      setMedia((items) => items.map((item) => (item.id === id ? result.media : item)));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "Unable to update media.");
    }
  };

  if (loading) {
    return <div className="ivory-card rounded-lg p-10 text-center font-bold text-ktd-muted">Loading Memory Media Vault workspace...</div>;
  }

  if (!vault) {
    return (
      <div className="grid gap-8">
        <PageHeader
          eyebrow="Admin Project Tools / Memory Media Vault"
          title="Memory Media Vault setup and approval workspace."
          subtitle="Create a client and event vault before opening its media workspace."
        />
        <section className="ivory-card rounded-lg p-10 text-center">
          <FolderKanban className="mx-auto size-10 text-[#087f7c]" />
          <h2 className="mt-5 font-display text-3xl font-bold">No event vault is ready yet.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-ktd-muted">{workspaceError}</p>
          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/admin/clients"
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg bg-ktd-black px-5 py-3 text-sm font-extrabold text-ktd-softGold"
            >
              <Users className="size-4" />
              Create Client
            </Link>
            <Link
              to="/admin/events"
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg border border-[#0f9f9a]/35 bg-white px-5 py-3 text-sm font-extrabold text-[#075f5c]"
            >
              <CalendarPlus className="size-4" />
              Create Event Vault
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Admin Project Tools / Memory Media Vault"
        title="Memory Media Vault setup and approval workspace."
        subtitle="Premium Memory Media Vault for client-owned storage setup, guest uploads, admin approval, and event-based media organization."
      >
        <div className="vault-rail rounded-lg border border-[#16a6a2]/35 bg-white/[0.07] p-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#7ee7e2]">{vault.enhancementLabel}</p>
          <label className="mt-4 grid gap-2 text-sm font-bold text-white/78">
            Vault status
            <select
              value={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value as VaultStatus)}
              className="focus-ring rounded-lg border border-[#16a6a2]/40 bg-[#062f31] px-3 py-2 text-sm font-extrabold text-white"
            >
              {vaultStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </PageHeader>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminVaultStat icon={ShieldCheck} label="Vault Status" value={vaultStatusLabel[selectedStatus]} />
        <AdminVaultStat icon={UploadCloud} label="Pending Review" value={String(pendingMedia.length || vault.pendingUploads)} />
        <AdminVaultStat icon={BadgeCheck} label="Approved Media" value={String(vault.approvedUploads)} />
        <AdminVaultStat icon={HardDrive} label="Storage Used" value={formatBytes(vault.storageBytes)} />
      </section>

      <section className="grid gap-6 xl:grid-cols-[1fr_.92fr]">
        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#087f7c]">Admin Setup Workflow</p>
              <h2 className="mt-3 font-display text-3xl font-bold">Create and connect the vault.</h2>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-ktd-muted">
                Create client-owned Memory Media Vault workspaces, connect event folders, and generate guest upload links
                without creating separate apps for each client.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void regenerateLink()}
              disabled={busy || !selectedEvent}
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg bg-[#0f9f9a] px-5 py-3 text-sm font-extrabold text-white shadow-[0_16px_44px_rgba(15,159,154,.22)] transition hover:bg-[#0b827f]"
            >
              <Plus className="size-4" />
              Generate Guest Link
            </button>
          </div>
          <div className="mt-6 grid gap-3">
            {vault.adminSetupSteps.map((step) => (
              <div key={step.label} className="rounded-lg border border-[#0f9f9a]/18 bg-white p-4">
                <div className="flex items-start gap-3">
                  {step.done ? (
                    <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-[#0f9f9a]" />
                  ) : (
                    <ClipboardList className="mt-0.5 size-5 shrink-0 text-[#8a6c14]" />
                  )}
                  <div>
                    <p className="text-sm font-extrabold text-ktd-rich">{step.label}</p>
                    {step.helper && <p className="mt-1 text-sm leading-6 text-ktd-muted">{step.helper}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {draftMessage && <p className="mt-5 rounded-lg bg-[#e6fbfa] p-4 text-sm font-bold text-[#075f5c]">{draftMessage}</p>}
        </article>

        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <FolderKanban className="size-9 text-[#087f7c]" />
          <h2 className="mt-5 font-display text-3xl font-bold">Vault Project Setup</h2>
          <form onSubmit={createVault} className="mt-5 grid gap-4">
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Client
              <select
                value={selectedClientId}
                onChange={(event) => setSelectedClientId(event.target.value)}
                className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-bold"
                required
              >
                {clientOptions.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Event / vault name
              <input
                value={eventName}
                onChange={(event) => setEventName(event.target.value)}
                className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                placeholder={vault.connectedMemoryHubName}
                required
              />
            </label>
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Vault display name
              <input
                value={vaultDisplayName}
                onChange={(event) => setVaultDisplayName(event.target.value)}
                className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                placeholder="Shantrice's 50th Birthday Vault"
              />
            </label>
            <div className="grid gap-4 md:grid-cols-3">
              <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
                Event slug
                <input
                  value={eventSlug}
                  onChange={(event) => setEventSlug(event.target.value)}
                  className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                  placeholder="wedding-vault"
                />
              </label>
              <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
                Event type
                <input
                  value={eventType}
                  onChange={(event) => setEventType(event.target.value)}
                  className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                  placeholder="Wedding, Birthday, Reunion"
                />
              </label>
              <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
                Event date
                <input
                  value={eventDate}
                  onChange={(event) => setEventDate(event.target.value)}
                  type="date"
                  className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                />
              </label>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
                Cover image URL
                <input
                  value={coverImage}
                  onChange={(event) => setCoverImage(event.target.value)}
                  className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                  placeholder="https://..."
                />
              </label>
              <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
                Optional logo/photo URL
                <input
                  value={optionalLogo}
                  onChange={(event) => setOptionalLogo(event.target.value)}
                  className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                  placeholder="https://..."
                />
              </label>
              <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
                Accent color
                <input
                  value={accentColor}
                  onChange={(event) => setAccentColor(event.target.value)}
                  className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                  placeholder="#D4AF37"
                />
              </label>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
                Primary color
                <input
                  value={primaryThemeColor}
                  onChange={(event) => setPrimaryThemeColor(event.target.value)}
                  className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                  placeholder="#050505"
                />
              </label>
              <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
                Secondary color
                <input
                  value={secondaryThemeColor}
                  onChange={(event) => setSecondaryThemeColor(event.target.value)}
                  className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                  placeholder="#FFF9EF"
                />
              </label>
            </div>
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Welcome message
              <textarea
                value={welcomeMessage}
                onChange={(event) => setWelcomeMessage(event.target.value)}
                className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                rows={3}
                placeholder="Optional client-facing welcome copy for this vault."
              />
            </label>
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Guest upload instructions
              <textarea
                value={guestUploadInstructions}
                onChange={(event) => setGuestUploadInstructions(event.target.value)}
                className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                rows={3}
                placeholder="Optional instructions shown on the guest upload page."
              />
            </label>
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Upload button text
              <input
                value={uploadButtonText}
                onChange={(event) => setUploadButtonText(event.target.value)}
                className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                placeholder="Upload Memories"
              />
            </label>
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Approval notice
              <textarea
                value={approvalNotice}
                onChange={(event) => setApprovalNotice(event.target.value)}
                className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                rows={3}
                placeholder="Your photos and videos will be reviewed before they appear in the Memory Media Vault."
              />
            </label>
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Confirmation message
              <textarea
                value={confirmationMessage}
                onChange={(event) => setConfirmationMessage(event.target.value)}
                className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
                rows={3}
                placeholder="Thank you. Your upload has been received and will be reviewed before it appears in the Memory Media Vault."
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg bg-[#0f9f9a] px-4 py-3 text-sm font-extrabold text-white disabled:opacity-50"
            >
              <CalendarPlus className="size-4" />
              Create / Update Event Vault
            </button>
          </form>
          <div className="mt-6 grid gap-4">
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Existing event / project folder
              <select
                value={selectedEventId}
                onChange={(event) => setSelectedEventId(event.target.value)}
                className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-bold"
              >
                {vault.events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.eventName}
                  </option>
                ))}
              </select>
            </label>
            {selectedGuestLink && (
              <div className="rounded-lg border border-[#0f9f9a]/18 bg-white p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#087f7c]">Current Guest Upload Link</p>
                <p className="mt-2 break-all text-sm font-bold text-ktd-rich">{selectedGuestLink.url}</p>
              </div>
            )}
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <Link
              to={vault.connectedMemoryHubUrl}
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg bg-[#0f9f9a] px-4 py-3 text-sm font-extrabold text-white"
            >
              <Link2 className="size-4" />
              Open Memory Media Vault
            </Link>
            <Link
              to="/admin/media"
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg border border-[#0f9f9a]/35 px-4 py-3 text-sm font-extrabold text-[#075f5c]"
            >
              <FileCheck2 className="size-4" />
              Review All Media
            </Link>
          </div>
        </article>
      </section>

      <section className="grid gap-6 xl:grid-cols-[.9fr_1.1fr]">
        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#087f7c]">Event-Based Vault Setup</p>
              <h2 className="mt-3 font-display text-3xl font-bold">Organize files by project or event.</h2>
            </div>
            <CalendarPlus className="size-9 text-[#087f7c]" />
          </div>
          <div className="mt-6 grid gap-4">
            {vault.events.map((event) => (
              <div key={event.id} className="rounded-lg border border-[#0f9f9a]/18 bg-white p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="font-extrabold text-ktd-rich">{event.eventName}</p>
                    <p className="mt-1 text-sm text-ktd-muted">{event.projectName}</p>
                    <p className="mt-2 text-xs font-bold uppercase tracking-[0.14em] text-[#087f7c]">
                      {event.eventDate ? formatDate(event.eventDate) : "Future setup option"}
                    </p>
                  </div>
                  <span className="status-pill border-[#0f9f9a]/25 bg-[#e6fbfa] text-[#075f5c]">
                    {vaultStatusLabel[event.status]}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs font-bold text-ktd-muted">
                  <span className="rounded-lg bg-[#f2fbfa] p-3">{event.pendingUploads} pending</span>
                  <span className="rounded-lg bg-[#f2fbfa] p-3">{event.approvedUploads} approved</span>
                  <span className="rounded-lg bg-[#f2fbfa] p-3">{formatBytes(event.storageBytes)}</span>
                </div>
              </div>
            ))}
          </div>
        </article>

        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#087f7c]">Storage Usage</p>
              <h2 className="mt-3 font-display text-3xl font-bold">Cloudflare R2 storage details.</h2>
            </div>
            <Cloud className="size-9 text-[#087f7c]" />
          </div>
          <div className="mt-6">
            <div className="flex items-center justify-between gap-4 text-sm font-bold text-ktd-rich">
              <span>{formatBytes(vault.storageBytes)} used</span>
              <span>{formatBytes(vault.storageLimitBytes)} configured limit</span>
            </div>
            <div className="mt-3 h-3 overflow-hidden rounded-full bg-white">
              <div className="h-full rounded-full bg-[#0f9f9a]" style={{ width: `${storagePercent}%` }} />
            </div>
            <p className="mt-3 text-sm font-semibold text-ktd-muted">{storagePercent}% of the configured storage limit.</p>
          </div>
          <dl className="mt-6 grid gap-3">
            {vault.r2Placeholders.map((item) => (
              <div key={item.label} className="rounded-lg border border-[#0f9f9a]/14 bg-white p-4">
                <dt className="text-xs font-black uppercase tracking-[0.18em] text-[#087f7c]">{item.label}</dt>
                <dd className="mt-2 break-words text-sm font-bold text-ktd-rich">{item.value}</dd>
              </div>
            ))}
          </dl>
        </article>
      </section>

      <section className="ivory-card rounded-lg p-6 sm:p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#087f7c]">Admin Approval Area</p>
            <h2 className="mt-3 font-display text-3xl font-bold">Uploaded files waiting for review.</h2>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-ktd-muted">
              Use these controls to approve, hide, feature, or not approve uploaded media across client vaults.
            </p>
          </div>
          <Link
            to={selectedEvent?.uploadUrl || vault.guestUploadUrl}
            className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg bg-[#0f9f9a] px-5 py-3 text-sm font-extrabold text-white"
          >
            <UploadCloud className="size-4" />
            Open Guest Upload
          </Link>
        </div>
        <div className="mt-6 grid gap-4">
          {(pendingMedia.length ? pendingMedia : recentMedia).map((item) => (
            <div key={item.id} className="grid gap-4 rounded-lg border border-[#0f9f9a]/16 bg-white p-4 lg:grid-cols-[1fr_auto] lg:items-center">
              <div className="flex min-w-0 items-start gap-4">
                <span className="grid size-12 shrink-0 place-items-center rounded-lg bg-[#062f31] text-[#7ee7e2]">
                  {item.fileType === "photo" ? <UploadCloud className="size-5" /> : <Database className="size-5" />}
                </span>
                <div className="min-w-0">
                  <p className="break-words font-extrabold text-ktd-rich">{item.fileName}</p>
                  <p className="mt-1 text-sm text-ktd-muted">
                    Uploaded by {item.uploaderName || "Guest"} - {formatBytes(item.fileSize)} - {formatDate(item.createdAt)}
                  </p>
                  <span className={`status-pill mt-3 ${statusClass(item.status, item.featured)}`}>
                    {item.featured ? "Featured" : statusLabel[item.status]}
                  </span>
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={() => void updateReviewStatus(item.id, "approve")}
                  className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg bg-[#0f9f9a] px-4 py-2 text-xs font-extrabold text-white"
                >
                  <CheckCircle2 className="size-4" />
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => void updateReviewStatus(item.id, "not-approve")}
                  className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-xs font-extrabold text-red-800"
                >
                  <XCircle className="size-4" />
                  Reject
                </button>
                <button
                  type="button"
                  onClick={() => void updateReviewStatus(item.id, "hide")}
                  className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg border border-[#0f9f9a]/30 bg-white px-4 py-2 text-xs font-extrabold text-[#075f5c]"
                >
                  <LockKeyhole className="size-4" />
                  Keep Private
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <InstructionPanel icon={Users} title="Role-Based Access">
          <p>Admins can create event vaults, review all uploads, and manage storage setup notes.</p>
          <p>Clients can view their event vault, share upload links, review status, and request additional vaults.</p>
          <p>Guests and family only use the shareable upload page and do not receive portal access.</p>
        </InstructionPanel>
        <InstructionPanel icon={ShieldCheck} title="Admin Instructions">
          {vault.adminInstructions.map((instruction) => (
            <p key={instruction}>{instruction}</p>
          ))}
        </InstructionPanel>
      </section>
    </div>
  );
}

function AdminVaultStat({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <article className="ivory-card rounded-lg p-5">
      <Icon className="size-7 text-[#087f7c]" />
      <p className="mt-4 text-sm font-bold text-ktd-muted">{label}</p>
      <strong className="mt-2 block text-2xl font-black text-ktd-rich">{value}</strong>
    </article>
  );
}

function InstructionPanel({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children: ReactNode }) {
  return (
    <article className="rounded-lg bg-[#062f31] p-6 text-white shadow-luxe sm:p-8">
      <Icon className="size-9 text-[#7ee7e2]" />
      <h2 className="mt-5 font-display text-3xl font-bold">{title}</h2>
      <div className="mt-4 grid gap-3 text-sm leading-6 text-white/72">{children}</div>
    </article>
  );
}
